import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Shield, TrendingUp, Calendar, FileText, MessageSquare,
  Heart, GraduationCap, Lock, Check, ArrowRight, BookOpen,
  Search, Layers, Clock, ChevronRight, ChevronLeft, Users, Tag, X as XIcon,
  Captions, Video, Sparkles, Award, BarChart3, Stamp, CalendarHeart, AlertTriangle, ArrowRightLeft, Trophy
} from 'lucide-react';
import API from '../utils/api';

var PLAN_COLORS = {
  Free:     { accent: '#6B6690', border: 'rgba(107,102,144,0.28)', bg: 'rgba(107,102,144,0.05)', cls: 'plan-card-free' },
  Basic:    { accent: '#7C7DB8', border: 'rgba(124,125,184,0.32)', bg: 'rgba(124,125,184,0.06)', cls: 'plan-card-basic' },
  Standard: { accent: '#5A4E9E', border: 'rgba(90,78,158,0.40)',   bg: 'rgba(90,78,158,0.07)',   cls: 'plan-card-standard' },
  Pro:      { accent: '#2D2560', border: 'rgba(45,37,96,0.45)',    bg: 'rgba(45,37,96,0.08)',    cls: 'plan-card-pro' },
};

var TIER_COLORS = {
  beginner:     { accent: '#5A4E9E', bg: 'rgba(90,78,158,0.08)',  border: 'rgba(90,78,158,0.20)' },
  intermediate: { accent: '#3A3178', bg: 'rgba(58,49,120,0.08)',  border: 'rgba(58,49,120,0.20)' },
  expert:       { accent: '#2D2560', bg: 'rgba(45,37,96,0.08)',   border: 'rgba(45,37,96,0.24)' },
  master:       { accent: '#1E1A3E', bg: 'rgba(30,26,62,0.08)',   border: 'rgba(30,26,62,0.28)' },
};

var SERIES_ICONS = {
  'best-interests': Shield,
  'co-parenting': Users,
  'high-conflict': TrendingUp,
};

/* ============== FEATURE CARD (carousel of capabilities) ============== */
// Visual style mirrors SeriesCard below — same chrome, same hover lift —
// so the "Everything You Need" carousel reads as a natural sibling of the
// Mastery Course Series row. Cards are pure icon + text; no screenshots.
function FeatureCard({ feature, onCTA }) {
  var Icon = feature.icon;
  return (
    <div
      className="card-surface landing-card-glow rounded-2xl overflow-hidden group motion-lift hover:-translate-y-0.5 flex flex-col h-full"
      data-testid={'landing-feature-' + feature.id}
    >
      {/* Hero image strip — conveys the card's meaning at a glance. The
          dark-purple overlay keeps icon + tag legible against any photo and
          ties the card visually back into Parentum's palette. */}
      {feature.image && (
        <div className="relative w-full h-32 sm:h-36 overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#2D2560] via-[#5A4E9E] to-[#A8AAD4]">
          <img
            src={feature.image}
            alt={feature.imageAlt || ''}
            loading="lazy"
            onError={function(e) { e.target.style.display = 'none'; }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1738]/55 via-[#1A1738]/15 to-[#1A1738]/0" aria-hidden="true" />
          {feature.tag && (
            <span
              className="absolute top-3 right-3 px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-[0.08em] backdrop-blur-md"
              style={{ background: 'rgba(255,255,255,0.92)', color: '#2D2560', border: '1px solid rgba(45,37,96,0.18)' }}
            >
              {feature.tag}
            </span>
          )}
        </div>
      )}

      <div className="card-pad-sm flex-1 flex flex-col">
        {/* Icon + Title sit on the same row so the heading aligns with the
            icon's vertical center. */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#EEEFF8] border border-[#DDE1EE] motion-border group-hover:border-[#A8AAD4] flex-shrink-0">
            <Icon className="w-5 h-5 text-[#2D2560]" strokeWidth={2} />
          </div>
          <h3 className="text-xl leading-tight font-bold tracking-[-0.01em] text-[#1A1738]">{feature.title}</h3>
        </div>
        <p className="text-base leading-snug text-[#2D2560] mb-3">{feature.summary}</p>
        <ul className="space-y-2 mt-auto">
          {feature.bullets.map(function (b, bi) {
            return (
              <li key={bi} className="flex items-start gap-2 text-[15px] leading-[20px] text-[#1A1738]">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2D2560]" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function SeriesCard({ series, onExplore }) {
  var Icon = SERIES_ICONS[series.series_id] || GraduationCap;
  var totalLessons = series.tiers.reduce(function (sum, t) { return sum + (t.total_lessons || 0); }, 0);
  var totalHours   = series.tiers.reduce(function (sum, t) { return sum + (t.estimated_hours || 0); }, 0);
  var freeCount    = series.tiers.filter(function (t) { return t.price === 0; }).length;

  return (
    <div className="card-surface landing-card-glow rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
      data-testid={'landing-series-' + series.series_id}>
      <div className="p-6 pb-4 flex-1 flex flex-col">
        {/* Header row mirrors the FeatureCard pattern in the carousel above:
            icon + title sit on the same line, with the Free Tier badge anchored
            to the far right of the same row. */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#EEEFF8] border border-[#DDE1EE] group-hover:border-[#A8AAD4] transition-colors flex-shrink-0">
            <Icon className="w-5 h-5 text-[#2D2560]" strokeWidth={2} />
          </div>
          <h3 className="text-xl leading-tight font-bold tracking-[-0.01em] text-[#1A1738] flex-1 min-w-0">{series.series_title}</h3>
          {freeCount > 0 && (
            <span className="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-[0.08em] backdrop-blur-md flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.92)', color: '#2D2560', border: '1px solid rgba(45,37,96,0.18)' }}>
              Free Tier
            </span>
          )}
        </div>
        <p className="text-base leading-snug text-[#1A1738] mb-4">{series.series_subtitle}</p>

        <div className="flex items-center gap-4 text-sm text-[#1A1738] mb-5">
          <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />{series.tiers.length} tiers</span>
          <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" />{totalLessons} lessons</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{totalHours}h total</span>
        </div>

        <div className="space-y-2">
          {series.tiers.map(function (t) {
            var tc = TIER_COLORS[t.tier] || TIER_COLORS.beginner;
            return (
              <div key={t.tier} className="flex items-center gap-3 p-2.5 rounded-lg border transition-colors"
                style={{ borderColor: tc.border, background: tc.bg }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: tc.accent }}></span>
                <span className="text-sm font-semibold flex-1 truncate" style={{ color: tc.accent }}>{t.title}</span>
                <span className="text-sm font-medium text-[#1A1738]">{t.total_lessons} lessons</span>
              </div>
            );
          })}
        </div>

        {/* Unbundled single-series price block — anchored to the bottom so it
            aligns visually with the bundle price row on the All-3 card. */}
        <div className="mt-auto pt-4">
          <div className="flex items-center justify-between p-2.5 rounded-lg border border-[#DDE1EE] bg-[#F6F7FC]">
            <span className="text-sm font-semibold text-[#1A1738] flex-1 truncate">Single series</span>
            <span className="text-base font-bold text-[#2D2560]">$150 <span className="text-xs font-medium text-[#1A1738]">/ $100 Pro</span></span>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 pt-2">
        <button onClick={function () { onExplore(series.series_id); }}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 group/btn nebula-gradient"
          data-testid={'explore-series-' + series.series_id}>
          Explore Series <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}

// Fourth card on the landing page — the All-3 Series Mega Bundle. Designed to
// match `SeriesCard` dimensions so it slots cleanly into the carousel
// alongside the three individual series cards.
function MegaBundleCard({ seriesList, onExplore }) {
  var totalLessons = seriesList.reduce(function (sum, s) {
    return sum + s.tiers.reduce(function (a, t) { return a + (t.total_lessons || 0); }, 0);
  }, 0);
  var totalHours = seriesList.reduce(function (sum, s) {
    return sum + s.tiers.reduce(function (a, t) { return a + (t.estimated_hours || 0); }, 0);
  }, 0);
  return (
    <div className="card-surface landing-card-glow rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
      data-testid="landing-series-all-bundle">
      <div className="p-6 pb-4 flex-1 flex flex-col">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#EEEFF8] border border-[#DDE1EE] group-hover:border-[#A8AAD4] transition-colors flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#2D2560]" strokeWidth={2} />
          </div>
          <h3 className="text-xl leading-tight font-bold tracking-[-0.01em] text-[#1A1738] flex-1 min-w-0">All-3 Mastery Bundle</h3>
          <span className="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-[0.08em] flex-shrink-0 text-white"
            style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
            Best Value
          </span>
        </div>
        <p className="text-base leading-snug text-[#1A1738] mb-4">Every Mastery Series in one purchase — best price for any plan.</p>

        <div className="flex items-center gap-4 text-sm text-[#1A1738] mb-4">
          <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />3 series</span>
          <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" />{totalLessons} lessons</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{totalHours}h total</span>
        </div>

        {/* Included courses — styled to match the tier-list rows on the
            individual SeriesCards (same padding, accent dot, font weight, and
            right-aligned lesson count) so all four cards read as siblings. */}
        <div className="space-y-2 mb-4">
          {seriesList.map(function (s, si) {
            var palette = [TIER_COLORS.beginner, TIER_COLORS.intermediate, TIER_COLORS.expert];
            var tc = palette[si % palette.length];
            var lessons = s.tiers.reduce(function (a, t) { return a + (t.total_lessons || 0); }, 0);
            return (
              <div key={s.series_id} className="flex items-center gap-3 p-2.5 rounded-lg border transition-colors"
                style={{ borderColor: tc.border, background: tc.bg }}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: tc.accent }}></span>
                <span className="text-sm font-semibold flex-1 truncate" style={{ color: tc.accent }}>{s.series_title}</span>
                <span className="text-sm font-medium text-[#1A1738]">{lessons} lessons</span>
              </div>
            );
          })}
        </div>

        {/* Single consolidated price row — pinned to the bottom so it lines up
            with the SeriesCard single-series price row across all four cards. */}
        <div className="mt-auto">
          <div className="flex items-center justify-between p-2.5 rounded-lg border border-[#10B981]/40 bg-[#D1FAE5]">
            <span className="text-sm font-semibold text-[#065F46] flex-1 truncate">Bundle</span>
            <span className="text-base font-bold text-[#065F46]">$300 <span className="text-xs font-medium text-[#047857]">/ $225 For Pro</span></span>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 pt-2">
        <button onClick={function () { onExplore('all-bundle'); }}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 group/btn nebula-gradient"
          data-testid="explore-series-all-bundle">
          Get the Bundle <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}

var Landing = function () {
  var auth = useAuth();
  var user = auth.user;
  var navigate = useNavigate();
  var [seriesData, setSeriesData] = useState([]);
  var featureScrollerRef = useRef(null);
  var [activeFeatureIdx, setActiveFeatureIdx] = useState(0);
  var activeIdxRef = useRef(0);
  // Scroll the feature carousel by one card. Uses scrollIntoView({inline:'center'})
  // so even the first and last cards can come fully into view — fixes the
  // "arrows don't reach the last card" issue caused by snap-start + container
  // padding eating the available scroll range.
  var scrollToFeature = function (idx) {
    var el = featureScrollerRef.current;
    if (!el) return;
    var cards = el.querySelectorAll('[data-feature-card]');
    var clamped = Math.max(0, Math.min(cards.length - 1, idx));
    if (!cards[clamped]) return;
    cards[clamped].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    activeIdxRef.current = clamped;
    setActiveFeatureIdx(clamped);
  };
  var scrollFeatures = function (direction) {
    scrollToFeature(activeIdxRef.current + direction);
  };
  // Update active index as the user scrolls — drives the pagination dots.
  // Use centerline matching so the dot reflects which card is centered.
  useEffect(function () {
    var el = featureScrollerRef.current;
    if (!el) return;
    var onScroll = function () {
      var cards = el.querySelectorAll('[data-feature-card]');
      if (!cards.length) return;
      // Edge cases: when scrolled to either extreme, force-pin the active
      // index to the first/last card. Otherwise small cards near the edge
      // would never light up because they can't physically center on screen.
      var maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft <= 5) {
        activeIdxRef.current = 0;
        setActiveFeatureIdx(0);
        return;
      }
      if (el.scrollLeft >= maxScroll - 5) {
        activeIdxRef.current = cards.length - 1;
        setActiveFeatureIdx(cards.length - 1);
        return;
      }
      var center = el.scrollLeft + el.clientWidth / 2;
      var closestIdx = 0;
      var minDist = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var cardCenter = cards[i].offsetLeft + cards[i].offsetWidth / 2;
        var d = Math.abs(cardCenter - center);
        if (d < minDist) { minDist = d; closestIdx = i; }
      }
      activeIdxRef.current = closestIdx;
      setActiveFeatureIdx(closestIdx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return function () { el.removeEventListener('scroll', onScroll); };
  }, []);
  // On first mount, center the carousel on its middle card so the layout
  // visually balances with the same number of preceding and following cards.
  useEffect(function () {
    var t = setTimeout(function () {
      var el = featureScrollerRef.current;
      if (!el) return;
      var cards = el.querySelectorAll('[data-feature-card]');
      if (!cards.length) return;
      var mid = Math.floor(cards.length / 2);
      cards[mid].scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
      activeIdxRef.current = mid;
      setActiveFeatureIdx(mid);
    }, 250);
    return function () { clearTimeout(t); };
  }, []);
  var [billingInterval, setBillingInterval] = useState('yearly');
  var [courseBundles, setCourseBundles] = useState({ free: 150, basic: 150, standard: 150, pro: 100 });

  useEffect(function () {
    axios.get(API + '/courses/public/series').then(function (r) {
      setSeriesData(r.data || []);
    }).catch(function () {});
  }, []);

  useEffect(function() {
    axios.get(API + '/courses/pricing', { withCredentials: true })
      .then(function(r) {
        var tb = r.data?.series_pricing?.[0]?.bundle_pricing?.tier_bundles;
        if (tb) setCourseBundles(tb);
      }).catch(function() {});
  }, []);

  // Course bundle pricing — overridden by backend if API returns values.
  var plansData = [
    { name: 'Free',     monthly: 0,  yearly: 0,   savings: 0,   features: ['Full calendar — free for 14 days', 'One free plan scan/mo', '3 journal entries/mo', 'Each course series: $' + courseBundles.free + ' · All-3 bundle: $300'], cta: 'Get Started' },
    { name: 'Basic',    monthly: 9.99,  yearly: 99.90,  savings: 19.98, features: ['All features unlocked', 'Co-parent messaging — free', '2 plan builder uses/mo', 'Each course series: $' + courseBundles.basic + ' · All-3 bundle: $300', 'Co-parent linking'], cta: 'Start Trial' },
    { name: 'Standard', monthly: 24.99, yearly: 249.90, savings: 49.98, features: ['All features unlocked', 'Co-parent messaging — free', '5 plan builders + scans/mo', 'Each course series: $' + courseBundles.standard + ' · All-3 bundle: $300', '200 video-call min/mo · unlimited calls', 'Compliance tracking'], cta: 'Start Trial', popular: true },
    { name: 'Pro',      monthly: 34.99, yearly: 349.90, savings: 69.98, features: ['Everything unlimited', 'Co-parent messaging — free', 'Each course series: $' + courseBundles.pro + ' · All-3 bundle: $225', '450 video-call min/mo · unlimited calls', 'Priority support'], cta: 'Start Trial', bestValue: true },
  ];

  var plans = plansData.map(function (p) {
    var price = billingInterval === 'yearly' ? p.yearly : p.monthly;
    // On the yearly plan, video-call minutes are unlocked upfront for the full year
    // (12 × monthly allotment) rather than metered month-by-month. Swap the relevant
    // feature line so users see the bigger headline number on the yearly toggle.
    var features = p.features;
    if (billingInterval === 'yearly' && (p.name === 'Standard' || p.name === 'Pro')) {
      var monthlyMin = p.name === 'Standard' ? 200 : 450;
      var yearlyMin = monthlyMin * 12;
      features = p.features.map(function (f) {
        if (/video-call/i.test(f)) {
          return yearlyMin.toLocaleString() + ' video-call min upfront for the year · unlimited calls';
        }
        return f;
      });
    }
    return Object.assign({}, p, {
      features: features,
      price: price === 0 ? '$0' : '$' + price,
      period: price === 0 ? '' : billingInterval === 'yearly' ? '/yr' : '/mo',
    });
  });

  var handleCTA       = function () { navigate(user ? '/dashboard' : '/signup'); };
  var handleLearnMore = function () { document.getElementById('tools').scrollIntoView({ behavior: 'smooth' }); };
  var handleTrialCTA  = function (planName) {
    var planId = planName.toLowerCase();
    if (!user) {
      navigate('/signup?plan=' + planId + '&trial=true');
      return;
    }
    var originUrl = window.location.origin;
    var payload = { plan: planId, interval: billingInterval, trial: true, origin_url: originUrl };
    axios.post(API + '/billing/create-checkout', payload, { withCredentials: true })
      .then(function (r) { if (r.data.url) window.location.href = r.data.url; })
      .catch(function (err) { alert(err.response?.data?.detail || 'Could not start trial'); });
  };
  var handleExploreSeries = function (seriesId) {
    navigate('/signup?from=courses' + (seriesId ? '&series=' + encodeURIComponent(seriesId) : ''));
  };

  // 10 feature cards rendered as a horizontal scroll carousel — Zoom.com-style:
  // arrow buttons + scroll-snap so users can swipe through every capability.
  // Each card has a hero image (Unsplash, w=600&q=80) that conveys meaning
  // visually without leaning on emojis or generic placeholders.
  var features = [
    {
      id: 'plan-builder',
      icon: FileText,
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Stack of structured legal documents',
      title: 'Parenting Plan Builder',
      summary: 'Build a custom, court-ready parenting plan across 14 guided stages.',
      bullets: [
        'Jurisdiction, custody, schedule, holidays, travel, safety & 8 more stages',
        'Strict legal-grammar engine that title-cases names and formats clauses',
        'Generates a polished, court-ready PDF with a full 4-week overnight grid',
      ],
      cta: 'Build a plan',
      tag: 'Court-ready',
    },
    {
      id: 'plan-analyzer',
      icon: Search,
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Magnifying glass reviewing a document',
      title: 'Plan Analyzer',
      summary: 'AI risk scan of any existing parenting plan — paste it in and get a report.',
      bullets: [
        'Flags vague clauses, missing tie-breakers and enforcement gaps',
        'Highlights safety concerns and ambiguous exchange language',
        'Plain-English recommendations sorted high → low severity',
      ],
      cta: 'Analyze a plan',
    },
    {
      id: 'coparent-link',
      icon: Users,
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Two co-parents collaborating',
      title: 'Co-Parent Linking',
      summary: 'Securely connect with your co-parent to share calendars, exchanges and records.',
      bullets: [
        'Send a one-time invite link — your co-parent joins with their own account',
        'Both parents see the same calendar, change requests and shared documents',
        'Either side can unlink instantly — neither party can take the other hostage',
      ],
      cta: 'Send an invite',
      tag: 'Synced',
    },
    {
      id: 'comms-check',
      icon: MessageSquare,
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Hands texting calmly on a phone',
      title: 'Comms Check',
      summary: 'Tone-aware co-parent messaging that catches conflict before you hit send.',
      bullets: [
        'AI flags aggression, manipulation and passive-aggression in real time',
        'Suggests neutral rewrites that stay civil and child-focused',
        'Searchable, timestamped log — admissible for court use',
      ],
      cta: 'Try Comms Check',
    },
    {
      id: 'journal',
      icon: BookOpen,
      image: 'https://images.unsplash.com/photo-1517842645767-c639042777db?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Open journal with pen',
      title: 'Journal',
      summary: 'Document everything in neutral, court-friendly language with locked timestamps.',
      bullets: [
        'AI rewrites emotional entries into objective, factual statements',
        'Categorize by Communication, Safety, Finances, Education and more',
        'One-click attach: video-call transcripts saved verbatim as locked PDFs',
      ],
      cta: 'Open journal',
    },
    {
      id: 'calendar',
      icon: Calendar,
      image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Calendar page on a desk',
      title: 'Shared Calendar',
      summary: 'Synced co-parenting calendar with overnights, exchanges and supervised visits.',
      bullets: [
        'Click days to cycle through your overnight / co-parent / exchange colors',
        'Recurring patterns: weekly, bi-weekly, every other weekend, custom',
        'Track holidays, school events and supervised visit facilities',
      ],
      cta: 'View calendar',
    },
    {
      id: 'shared-events',
      icon: CalendarHeart,
      image: 'https://images.unsplash.com/photo-1542037104857-ffbb0b9155fb?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Family gathering for a shared event',
      title: 'Shared Events',
      summary: 'Propose events to your co-parent and lock the details once both sides confirm.',
      bullets: [
        'School plays, doctor visits, sports games, birthdays — fully shared',
        'Either parent can propose, the other accepts, declines or counter-proposes',
        'Locked events appear on both calendars with full audit trail',
      ],
      cta: 'Plan an event',
    },
    {
      id: 'scheduling-conflicts',
      icon: AlertTriangle,
      image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Calendar app on a laptop',
      title: 'Scheduling Conflicts',
      summary: 'Two-way Google Calendar sync surfaces conflicts before they become arguments.',
      bullets: [
        'One-click connect to each parent\'s Google Calendar — read-only by default',
        'AI flags overlaps, exchange-window clashes and holiday double-bookings',
        'Suggests resolutions both parents can accept with a single tap',
      ],
      cta: 'Connect Google Calendar',
      tag: 'Google sync',
    },
    {
      id: 'video-calls',
      icon: Video,
      image: 'https://images.unsplash.com/photo-1587560699334-cc4ff634909a?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Laptop showing a video call interface',
      title: 'Video Calls',
      summary: 'Privacy-first co-parent video calls. No audio or video is ever recorded.',
      bullets: [
        'Bilateral live captions — each browser transcribes its own mic',
        'Transcripts are encrypted at rest, never the call audio itself',
        'Built-in waiting room with consent before transcription starts',
      ],
      cta: 'Start a call',
      tag: 'Privacy-first',
    },
    {
      id: 'transcripts-pdf',
      icon: Captions,
      image: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Padlock resting on a closed document',
      title: 'Locked Call Transcripts',
      summary: 'Every call generates a verbatim, court-of-record PDF that cannot be edited.',
      bullets: [
        'No AI rewrite, no grammar check, no scoring — pure record',
        'Standard branded headers, footers and Parentum logo on every page',
        'Even declined calls produce a record so the timeline stays intact',
      ],
      cta: 'See sample',
    },
    {
      id: 'business-record-affidavit',
      icon: Stamp,
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Notary stamp on an affidavit',
      title: 'Business Record Affidavits',
      summary: "Turn your Parentum logs into admissible evidence with a one-click custodian affidavit request.",
      bullets: [
        'Request a notarized custodian-of-records affidavit for any export',
        'Bundles your messages, journal entries and call transcripts into one exhibit',
        'Pre-formatted to satisfy Rule 902(11) / state business-records standards',
      ],
      cta: 'Request affidavit',
      tag: 'Admissible',
    },
    {
      id: 'pdf-export',
      icon: Award,
      image: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Polished printed legal documents in a binder',
      title: 'Court-Ready PDF Export',
      summary: 'Plans, transcripts and compliance logs all share the same professional chrome.',
      bullets: [
        'Times-New-Roman legal typography with strict clause numbering',
        '0.5pt borders, consistent headers/footers and case-info tables',
        'Drop straight into a court bundle without reformatting',
      ],
      cta: 'View samples',
    },
    {
      id: 'progress',
      icon: BarChart3,
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Analytics chart with upward trends',
      title: 'Progress & Insights',
      summary: 'Track your co-parenting communication score over time with weekly trends.',
      bullets: [
        'Green → Blue → Yellow → Orange → Red color scoring on every message',
        'Streaks, badges and milestones that reward calm, child-focused tone',
        'Export progress reports for therapists, mediators or counsel',
      ],
      cta: 'See progress',
    },
    {
      id: 'progress-badges',
      icon: Award,
      image: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Earned achievement medals',
      title: 'Earn Progress Badges',
      summary: 'Gamified rewards that recognize calm, consistent, child-focused co-parenting.',
      bullets: [
        '20+ badges across Communication, Reliability, Boundaries and Empathy',
        'Streaks for on-time exchanges and conflict-free messaging weeks',
        'Share earned badges with mediators, counselors or in court filings',
      ],
      cta: 'See badges',
      tag: 'Gamified',
    },
    {
      id: 'courses',
      icon: GraduationCap,
      image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=600&q=80&auto=format&fit=crop',
      imageAlt: 'Open books for learning',
      title: 'Co-Parenting Mastery Courses',
      summary: 'Expert-built, tiered education to grow your skills at your own pace.',
      bullets: [
        'Free Foundations tier to start — no credit card required',
        'Standard and Pro tiers unlock advanced legal, financial and emotional modules',
        'Earn certificates of completion you can share with the court',
      ],
      cta: 'Browse courses',
    },
  ];

  var strips = [
    { icon: Shield,         title: 'Court-Ready Structure',   desc: 'Plans built for real-life situations' },
    { icon: Heart,          title: 'Reduce Conflict & Stress', desc: 'Tools that promote better communication' },
    { icon: Users,          title: 'Co-Parenting Linking',    desc: 'Connect with your co-parent for shared calendars, exchanges, and journaling' },
    { icon: Layers,         title: 'Parallel Parenting Tools', desc: 'Built for low-contact families — share what matters, skip the rest' },
    { icon: Captions,       title: 'Live Call Transcripts',   desc: 'Transcribe video calls in real time — no audio or video recorded' },
    { icon: GraduationCap,  title: 'Educational Not Legal Advice', desc: 'Empowering parents with knowledge and clarity' },
    { icon: Lock,           title: 'Secure & Private',        desc: 'Your data is protected every step of the way' },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden pb-[env(safe-area-inset-bottom)] landing-bg" data-testid="landing-root">
      {/* Ambient decorative layer — pointer-events:none so it never blocks UI.
          Built from the existing #2D2560 / #5A4E9E / #A8AAD4 / #EEEFF8 palette
          so it reads as one cohesive system, not a bolt-on. Radial "orbs" sit
          behind everything; a subtle grain SVG noise overlay sits on top of
          the orbs but still behind content. The strip section + footer cover
          themselves with solid bg, so the orbs only show through the lighter
          sections (hero, tools carousel, courses, pricing, testimonial). */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
        {/* Top-right bright purple highlight */}
        <div className="absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full opacity-65 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(168,170,212,0.55), rgba(168,170,212,0) 70%)' }} />
        {/* Mid-left warm purple glow */}
        <div className="absolute top-[55%] -left-40 w-[40rem] h-[40rem] rounded-full opacity-55 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(126,111,197,0.55), rgba(126,111,197,0) 72%)' }} />
        {/* Lower-right lavender wash */}
        <div className="absolute bottom-[8%] -right-44 w-[44rem] h-[44rem] rounded-full opacity-70 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(238,239,248,0.40), rgba(238,239,248,0) 70%)' }} />
        {/* Mid-right secondary purple orb for visual rhythm */}
        <div className="absolute top-[35%] right-[8%] w-[28rem] h-[28rem] rounded-full opacity-60 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(90,78,158,0.55), rgba(90,78,158,0) 70%)' }} />
        {/* Bottom-left soft lavender for footer-area depth */}
        <div className="absolute bottom-[2%] -left-32 w-[32rem] h-[32rem] rounded-full opacity-55 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, rgba(168,170,212,0.55), rgba(168,170,212,0) 70%)' }} />
        {/* Grain/noise overlay — adds tactile depth on flat gradients */}
        <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.9 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />
      </div>
      {/* ========== TOP NAV ========== */}
      <header className="relative z-20 bg-white border-b border-[#DDE1EE]" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5 flex justify-between items-center gap-3">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo-link">
            <div
              className="parentum-logo-gradient h-4 sm:h-5 lg:h-6"
              role="img"
              aria-label="Parentum"
              data-testid="header-logo"
              style={{
                WebkitMaskImage: 'url(/parentum-logo.png)',
                maskImage: 'url(/parentum-logo.png)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'left center',
                maskPosition: 'left center',
              }}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#3A3566]">
            <a href="#tools"    className="hover:text-[#2D2560] transition-colors">Features</a>
            <a href="#courses"  className="hover:text-[#2D2560] transition-colors">Courses</a>
            <a href="#pricing"  className="hover:text-[#2D2560] transition-colors">Pricing</a>
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="dashboard-link"
                className="px-4 py-2 sm:px-5 sm:py-2.5 text-sm font-semibold rounded-lg nebula-gradient hover:opacity-95 transition-opacity">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="login-link"
                  className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-[#2D2560] hover:text-[#3A3178] transition-colors">
                  Log In
                </Link>
                <Link to="/signup" data-testid="signup-link"
                  className="px-3 py-1.5 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold rounded-lg nebula-gradient cta-glow hover:opacity-95 transition-opacity whitespace-nowrap">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ========== HERO ========== */}
      <section
        className="hero-section relative z-10 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Responsive hero image — never exceeds container width.
            Uses width:100% + height:auto so the section's height tracks the
            image's natural aspect ratio at every viewport. No fixed pixel
            widths on the image or its wrapper. */}
        <img
          src="/hero-bg.png"
          alt=""
          aria-hidden="true"
          loading="eager"
          className="block w-full h-auto max-w-full select-none pointer-events-none"
        />

        {/* Headline + CTAs overlaid on the image */}
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10 lg:px-12 pt-6 sm:pt-8 lg:pt-10">
          <div className="max-w-7xl mx-auto w-full relative">
            <div className="theme-dark relative z-10 max-w-xl">
              <h1 className="font-serif-display text-[28px] sm:text-4xl md:text-5xl lg:text-7xl leading-[1.05] tracking-tight mb-3 sm:mb-5 lg:mb-6 text-white" data-testid="hero-title" style={{ textShadow: '0 2px 24px rgba(45,37,96,0.5)' }}>
                Co-Parenting,<br />Reimagined.
              </h1>
              <p className="hidden sm:block text-base lg:text-lg max-w-md mb-6 sm:mb-8 lg:mb-10 leading-relaxed text-white/90" data-testid="hero-description" style={{ textShadow: '0 1px 12px rgba(45,37,96,0.4)' }}>
                Structure your custody, reduce conflict, and stay court-ready.
              </p>
              <div className="flex flex-row items-center gap-2 sm:gap-3 max-w-md">
                <button onClick={handleCTA}
                  className="flex-1 sm:flex-initial px-3 sm:px-7 py-2 sm:py-3 lg:py-3.5 rounded-lg font-semibold text-xs sm:text-base bg-white hover:bg-[#EEEFF8] transition-all flex items-center justify-center gap-1.5 sm:gap-2 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] whitespace-nowrap"
                  style={{ color: '#2D2560' }}
                  data-testid="hero-cta-button">
                  Build Your Plan
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                <button onClick={handleLearnMore}
                  className="flex-1 sm:flex-initial px-3 sm:px-7 py-2 sm:py-3 lg:py-3.5 rounded-lg font-semibold text-xs sm:text-base bg-white/10 backdrop-blur-md border border-white/40 text-white hover:bg-white/20 transition-all whitespace-nowrap"
                  data-testid="learn-more-button">
                  See How It Works
                </button>
              </div>
              <p className="hidden sm:block text-xs text-white/75 mt-4 sm:mt-6 tracking-wide">
                7-day free trial &middot; No credit card required &middot; Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FEATURE STRIP (dark indigo band — continuous marquee) ========== */}
      <section className="relative z-10 theme-dark bg-[#2D2560] overflow-hidden" data-testid="strip-section">
        <div className="max-w-7xl mx-auto py-4 sm:py-5">
          <div className="strip-marquee" data-testid="strip-carousel">
            <div className="strip-marquee-track">
              {strips.concat(strips).map(function (s, i) {
                return (
                  <div key={i}
                    className="strip-marquee-item flex flex-row items-center gap-3"
                    aria-hidden={i >= strips.length ? 'true' : undefined}
                    data-testid={'strip-item-' + (i % strips.length)}>
                    <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-4 h-4 text-white" strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white leading-tight">{s.title}</h3>
                      <p className="text-xs text-white/70 leading-snug">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ========== TOOLS GRID ========== */}
      <section id="tools" className="relative z-10 py-8 sm:py-10 lg:py-14" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#FFFFFF] mb-3">
              Everything You Need, All in One Place
            </h2>
            <p className="text-[#DDE1EE] text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
              Powerful tools to help you co-parent with clarity, confidence, and control.
            </p>
          </div>
        </div>

        {/* Carousel breaks out of the max-w-7xl container so cards can scroll
            edge-to-edge across the full viewport (free-floating, no side
            framing). Arrow buttons sit on the carousel itself with a small
            viewport inset. */}
        <div className="relative">
          <div
            ref={featureScrollerRef}
            className="overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 pt-2 feature-carousel"
            style={{ scrollbarWidth: 'none' }}
            data-testid="feature-carousel"
          >
            <div className="flex gap-6 lg:gap-7">
              {features.map(function (f) {
                return (
                  <div
                    key={f.id}
                    data-feature-card
                    className="snap-center flex-shrink-0 w-[78vw] sm:w-[320px] lg:w-[340px]"
                  >
                    <FeatureCard feature={f} onCTA={handleCTA} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Controls row — prev arrow + pagination dots + next arrow sit
            together directly below the carousel so the navigation is grouped
            in one obvious place instead of hugging the card edges. */}
        <div className="max-w-7xl mx-auto px-6 mt-6 flex items-center justify-center gap-4" data-testid="feature-pagination">
          <button
            onClick={function () { scrollFeatures(-1); }}
            aria-label="Scroll features left"
            className="w-9 h-9 rounded-full bg-white border border-[#DDE1EE] shadow-sm flex items-center justify-center text-[#2D2560] hover:bg-[#EEEFF8] hover:border-[#A8AAD4] motion-bg motion-border"
            data-testid="feature-scroll-left"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </button>
          <div className="flex items-center justify-center gap-2">
            {features.map(function (f, i) {
              var active = i === activeFeatureIdx;
              return (
                <button
                  key={f.id}
                  onClick={function () { scrollToFeature(i); }}
                  aria-label={'Go to feature ' + (i + 1) + ': ' + f.title}
                  aria-current={active ? 'true' : 'false'}
                  className={'rounded-full motion-bg ' + (active
                    ? 'w-6 h-1.5 bg-[#FFFFFF]'
                    : 'w-1.5 h-1.5 bg-[rgba(255,255,255,0.35)] hover:bg-[rgba(255,255,255,0.65)]')}
                  data-testid={'feature-dot-' + i}
                />
              );
            })}
          </div>
          <button
            onClick={function () { scrollFeatures(1); }}
            aria-label="Scroll features right"
            className="w-9 h-9 rounded-full bg-white border border-[#DDE1EE] shadow-sm flex items-center justify-center text-[#2D2560] hover:bg-[#EEEFF8] hover:border-[#A8AAD4] motion-bg motion-border"
            data-testid="feature-scroll-right"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </section>

      {/* ========== TAMPER FREE RECORDS ========== */}
      <section className="relative z-10 py-10 sm:py-14 px-4 sm:px-6" data-testid="tamper-free-section">
        <div className="max-w-[1700px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2.1fr] gap-8 lg:gap-10 items-center">
            {/* Explanation copy - Left side */}
            <div className="order-2 lg:order-1 text-white">
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-4" style={{ color: '#FFFFFF' }}>
                Every record. Forever secure.
              </h2>
              <p className="text-[15px] leading-[22px] mb-5" style={{ color: 'rgba(255,255,255,0.92)' }}>
                Parentum cryptographically timestamps and immutably records every action, message, and document. Your co-parenting history becomes an unalterable record that holds up in court.
              </p>
              <button onClick={handleCTA}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#2D2560] text-sm font-semibold hover:bg-white/90 transition-colors">
                Start protecting your records <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Card with Text Content - Right side */}
            <div className="relative order-1 lg:order-2" data-testid="tamper-free-showcase">
              {/* Two-layer halo matching calendar section */}
              <div
                aria-hidden="true"
                className="absolute -inset-16 sm:-inset-24 rounded-[40px] pointer-events-none"
                style={{
                  background: 'radial-gradient(60% 60% at 50% 50%, rgba(45,37,96,0.95) 0%, rgba(58,49,120,0.70) 30%, rgba(90,78,158,0.40) 55%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(34px)',
                }}
              ></div>
              <div
                aria-hidden="true"
                className="absolute -inset-6 sm:-inset-8 rounded-[28px] pointer-events-none"
                style={{
                  background: 'radial-gradient(50% 50% at 50% 50%, rgba(45,37,96,0.85) 0%, rgba(58,49,120,0.55) 50%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(14px)',
                }}
              ></div>
              <div
                className="relative card-surface rounded-2xl overflow-hidden"
                style={{
                  boxShadow: '0 0 0 1px rgba(45,37,96,0.85), 0 0 40px rgba(45,37,96,0.7), 0 24px 80px -10px rgba(45,37,96,0.75), 0 0 120px rgba(58,49,120,0.5)',
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr]">
                  {/* Left side - Text content */}
                  <div className="p-8 sm:p-10 lg:p-12">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#EEEFF8] border border-[#DDE1EE]">
                        <Lock className="w-5 h-5 text-[#2D2560]" strokeWidth={2} />
                      </div>
                      <h3 className="font-serif-display text-3xl sm:text-4xl text-[#1A1738] font-bold">
                        Tamper Free Records
                      </h3>
                    </div>
                    <p className="text-base sm:text-lg text-[#2D2560] mb-6 leading-relaxed">
                      Every action, message, and document in Parentum is cryptographically timestamped and immutably recorded.
                    </p>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <div>
                          <p className="text-base text-[#1A1738] font-semibold">Court-Ready Documentation</p>
                          <p className="text-sm text-[#2D2560]/80">Generate verified PDFs with blockchain-backed timestamps for legal proceedings</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <div>
                          <p className="text-base text-[#1A1738] font-semibold">Immutable Communication History</p>
                          <p className="text-sm text-[#2D2560]/80">Every message and change request is permanently recorded and cannot be altered</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <div>
                          <p className="text-base text-[#1A1738] font-semibold">Video Call Transcripts</p>
                          <p className="text-sm text-[#2D2560]/80">Tamper free records of video calls to protect both parties from false allegations</p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Right side - Full-height image */}
                  <div className="relative min-h-[400px] md:min-h-0">
                    <img
                      src="https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxjeWJlciUyMHNlY3VyaXR5JTIwZW5jcnlwdGlvbiUyMGRpZ2l0YWwlMjBwcm90ZWN0aW9uJTIwbmV0d29ya3xlbnwwfHx8fDE3ODQyMzQ0OTR8MA&ixlib=rb-4.1.0&q=85"
                      alt="Blockchain network representing cyber security and tamper-free records"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Subtle gradient overlay for visual depth */}
                    <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-white/10" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CALENDAR SHOWCASE ========== */}
      <section className="relative z-10 py-10 sm:py-14 px-4 sm:px-6" data-testid="calendar-showcase-section">
        <div className="max-w-[1700px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[2.1fr_1fr] gap-8 lg:gap-10 items-center">
            {/* Real screenshot of the live May 2026 calendar panel (super-admin account).
                Wrapped in a magic purple glow so it visually feels like the calendar is
                "alive" on the marketing page. */}
            <div className="relative order-2 lg:order-1" data-testid="calendar-showcase-mock">
              {/* Two-layer halo so the glow really pops:
                  • A wide, soft radial bloom that fades into the section bg
                  • A tighter, brighter inner halo hugging the card edges */}
              <div
                aria-hidden="true"
                className="absolute -inset-16 sm:-inset-24 rounded-[40px] pointer-events-none"
                style={{
                  background: 'radial-gradient(60% 60% at 50% 50%, rgba(45,37,96,0.95) 0%, rgba(58,49,120,0.70) 30%, rgba(90,78,158,0.40) 55%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(34px)',
                }}
              ></div>
              <div
                aria-hidden="true"
                className="absolute -inset-6 sm:-inset-8 rounded-[28px] pointer-events-none"
                style={{
                  background: 'radial-gradient(50% 50% at 50% 50%, rgba(45,37,96,0.85) 0%, rgba(58,49,120,0.55) 50%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(14px)',
                }}
              ></div>
              <div
                className="relative card-surface p-2 sm:p-3 rounded-2xl"
                style={{
                  boxShadow: '0 0 0 1px rgba(45,37,96,0.85), 0 0 40px rgba(45,37,96,0.7), 0 24px 80px -10px rgba(45,37,96,0.75), 0 0 120px rgba(58,49,120,0.5)',
                }}
              >
                <img
                  src="/landing-calendar-may-2026.png"
                  alt="Live Parentum calendar — May 2026"
                  className="block w-full h-auto rounded-md"
                  loading="lazy"
                  data-testid="calendar-showcase-image"
                />
              </div>
            </div>

            {/* Explanation copy */}
            <div className="order-1 lg:order-2 text-white" data-testid="calendar-showcase-copy">
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-4" style={{ color: '#FFFFFF' }}>
                One calendar. Every overnight, exchange, and event.
              </h2>
              <p className="text-[15px] leading-[22px] mb-5" style={{ color: 'rgba(255,255,255,0.92)' }}>
                Parentum&rsquo;s calendar is the single source of truth for your co-parenting schedule. Apply a custody pattern once and every overnight is laid out for the year — color-coded so anyone in the household can see who has the kids tonight at a glance.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  { icon: CalendarHeart, label: 'Color-coded overnights so the whole month reads in one second' },
                  { icon: ArrowRightLeft, label: 'Exchange days highlighted automatically at every handoff' },
                  { icon: Sparkles, label: 'AI daily summary flags scheduling conflicts before they happen' },
                  { icon: Users, label: 'Share events with your co-parent' },
                  { icon: Calendar, label: 'Google Calendar Integration' },
                ].map(function (b, i) {
                  var I = b.icon;
                  return (
                    <li key={i} className="flex items-start gap-3 text-[15px] leading-[20px]" style={{ color: '#FFFFFF' }}>
                      <span className="w-7 h-7 rounded-full bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
                        <I className="w-3.5 h-3.5 text-white" strokeWidth={2.25} />
                      </span>
                      <span>{b.label}</span>
                    </li>
                  );
                })}
              </ul>
              <button onClick={handleCTA}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#2D2560] text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="calendar-showcase-cta">
                Try the calendar free <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== COURSE SERIES (conditional) ========== */}
      {/* Sits above the Certificate Showcase so visitors see the curriculum
          (what they're buying) before the proof-of-completion artwork. */}
      {seriesData.length > 0 && (
        <section id="courses" className="relative z-10 py-8 sm:py-10 lg:py-12 px-6" data-testid="courses-section">
          <div className="max-w-[1500px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#FFFFFF] mb-3">Mastery Course Series</h2>
              <p className="text-[#DDE1EE] text-base sm:text-lg max-w-2xl mx-auto">
                Structured, tiered education built by experts. Start free, progress at your pace, and earn certificates of completion.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {seriesData.map(function (s) {
                return <SeriesCard key={s.series_id} series={s} onExplore={handleExploreSeries} />;
              })}
              <MegaBundleCard seriesList={seriesData} onExplore={handleExploreSeries} />
            </div>
          </div>
        </section>
      )}

      {/* ========== CERTIFICATE SHOWCASE ========== */}
      {/* Mirrors the calendar showcase layout but reversed — copy on the LEFT,
          the live certificate art on the RIGHT — so the page rhythm alternates
          and the eye keeps moving down the section list. The "John Smith" name
          is overlaid in the same Great Vibes cursive used in-app, with
          generous horizontal padding so descenders never clip. */}
      <section className="relative z-10 py-10 sm:py-14 px-4 sm:px-6" data-testid="certificate-showcase-section">
        <div className="max-w-[1700px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2.1fr] gap-8 lg:gap-10 items-center">
            {/* Explanation copy — LEFT */}
            <div className="order-1 lg:order-1 text-white" data-testid="certificate-showcase-copy">
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-4" style={{ color: '#FFFFFF' }}>
                Court-ready certificates the moment you finish.
              </h2>
              <p className="text-[15px] leading-[22px] mb-5" style={{ color: 'rgba(255,255,255,0.92)' }}>
                Every Mastery Series ends with a final exam. Pass with 85% or higher and your personalized certificate unlocks instantly — issued by Co-Parenting Solutions, LLC d/b/a Parentum and printable as a PDF for your attorney, mediator, or judge.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  { icon: Award, label: 'Personalized in cursive with your full legal name' },
                  { icon: Shield, label: 'Issued by a real LLC — accepted by courts and mediators' },
                  { icon: FileText, label: 'One-click PDF download in landscape US Letter format' },
                  { icon: GraduationCap, label: 'Earn one per series — Best Interests, Co-Parenting, and High-Conflict Strategy' },
                  { icon: Trophy, label: 'Bundle certificate awarded for completing all three' },
                ].map(function (b, i) {
                  var I = b.icon;
                  return (
                    <li key={i} className="flex items-start gap-3 text-[15px] leading-[20px]" style={{ color: '#FFFFFF' }}>
                      <span className="w-7 h-7 rounded-full bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
                        <I className="w-3.5 h-3.5 text-white" strokeWidth={2.25} />
                      </span>
                      <span>{b.label}</span>
                    </li>
                  );
                })}
              </ul>
              <button onClick={handleCTA}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#2D2560] text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="certificate-showcase-cta">
                Start earning yours <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Certificate artwork — RIGHT, wrapped in the same purple halo as the
                calendar mock so it visually feels like the cert is "alive". */}
            <div className="relative order-2 lg:order-2" data-testid="certificate-showcase-mock">
              <div
                aria-hidden="true"
                className="absolute -inset-16 sm:-inset-24 rounded-[40px] pointer-events-none"
                style={{
                  background: 'radial-gradient(60% 60% at 50% 50%, rgba(45,37,96,0.95) 0%, rgba(58,49,120,0.70) 30%, rgba(90,78,158,0.40) 55%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(34px)',
                }}
              ></div>
              <div
                aria-hidden="true"
                className="absolute -inset-6 sm:-inset-8 rounded-[28px] pointer-events-none"
                style={{
                  background: 'radial-gradient(50% 50% at 50% 50%, rgba(45,37,96,0.85) 0%, rgba(58,49,120,0.55) 50%, rgba(45,37,96,0.0) 80%)',
                  filter: 'blur(14px)',
                }}
              ></div>
              <div
                className="relative card-surface p-2 sm:p-3 rounded-2xl"
                style={{
                  boxShadow: '0 0 0 1px rgba(45,37,96,0.85), 0 0 40px rgba(45,37,96,0.7), 0 24px 80px -10px rgba(45,37,96,0.75), 0 0 120px rgba(58,49,120,0.5)',
                }}
              >
                {/* Aspect-ratio container locks the cursive overlay to the
                    artwork's signature line. The bundle artwork is square
                    (1:1), so we use a square ratio here — different from the
                    landscape per-series certs. */}
                <div className="relative w-full overflow-hidden rounded-md" style={{ aspectRatio: '3300 / 2550' }}>
                  <img
                    src="/landing-certificate-bundled.png"
                    alt="Parentum bundled Mastery Series certificate awarded to John Smith"
                    className="block w-full h-auto"
                    loading="lazy"
                    data-testid="certificate-showcase-image"
                  />
                  <div
                    className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
                    style={{ top: '38%', paddingLeft: '12%', paddingRight: '12%' }}
                  >
                    <span
                      style={{
                        fontFamily: "'Great Vibes', cursive",
                        // Leave headroom above + below so cursive descenders
                        // (like the loop on "h") never clip.
                        fontSize: 'clamp(28px, 5.2cqw, 88px)',
                        lineHeight: 1.4,
                        color: '#1A1738',
                        padding: '0.2em 0',
                      }}
                      data-testid="certificate-showcase-name"
                    >
                      John Smith
                    </span>
                  </div>
                  {/* Diagonal "SAMPLE" watermark — sits above the artwork and
                      cursive name so visitors instantly understand this is a
                      preview, not a real issued credential. Low opacity keeps
                      the underlying design readable. */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
                    data-testid="certificate-showcase-watermark"
                  >
                    <span
                      style={{
                        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                        fontWeight: 800,
                        letterSpacing: '0.18em',
                        fontSize: 'clamp(48px, 14cqw, 220px)',
                        color: '#2D2560',
                        opacity: 0.16,
                        transform: 'rotate(-22deg)',
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                      }}
                    >
                      Sample
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PRICING ========== */}
      <section id="pricing" className="relative z-10 py-8 sm:py-10 lg:py-14 px-6" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#FFFFFF] mb-3">Choose Your Plan</h2>
            <p className="text-[#DDE1EE] text-sm sm:text-base lg:text-lg">Start with a 7-day free trial, cancel anytime</p>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-5 sm:mt-6 flex-wrap" data-testid="landing-interval-toggle">
              <span className={'text-sm font-medium transition-colors ' + (billingInterval === 'monthly' ? 'text-[#FFFFFF]' : 'text-[#A8AAD4]')}>Monthly</span>
              <button onClick={function () { setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly'); }}
                className="relative w-14 h-7 rounded-full transition-colors"
                style={{ background: billingInterval === 'yearly' ? 'linear-gradient(135deg, #ffffff, #DDE1EE)' : 'rgba(255,255,255,0.18)' }}
                data-testid="landing-billing-toggle">
                <div className={'absolute top-1 w-5 h-5 rounded-full bg-[#2D2560] transition-transform ' + (billingInterval === 'yearly' ? 'translate-x-8' : 'translate-x-1')}></div>
              </button>
              <span className={'text-sm font-medium transition-colors ' + (billingInterval === 'yearly' ? 'text-[#FFFFFF]' : 'text-[#A8AAD4]')}>Yearly</span>
              {billingInterval === 'yearly' && (
                <span
                  className="px-5 py-2 rounded-full text-base sm:text-lg font-bold uppercase tracking-wider text-white shadow-md"
                  style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                >
                  Save up to $70
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-stretch">
            {plans.map(function (plan, i) {
              var pc = PLAN_COLORS[plan.name];
              var ringCls = plan.bestValue
                ? ' ring-2 ring-[#2D2560]/55'
                : plan.popular ? ' ring-2 ring-[#5A4E9E]/45' : '';
              return (
                <div key={i} className={'card-surface landing-card-glow plan-card p-5 sm:p-6 flex flex-col h-full ' + pc.cls + ringCls}
                  data-testid={'pricing-plan-' + i}>
                  {/* Reserve a fixed badge slot on every card so heading rows align. */}
                  <div className="h-6 mb-3">
                    {plan.popular && (
                      <div className="inline-block px-2.5 py-0.5 bg-[#5A4E9E] text-white text-[10px] font-bold uppercase tracking-wider rounded-full" data-testid="badge-most-popular">Most Popular</div>
                    )}
                    {plan.bestValue && (
                      <div className="inline-block px-2.5 py-0.5 bg-[#2D2560] text-white text-[10px] font-bold uppercase tracking-wider rounded-full" data-testid="badge-best-value">Best Value</div>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-0.5" style={{ color: pc.accent }}>{plan.name}</h3>
                  <div className="mb-4 min-h-[56px]">
                    <span className="text-3xl font-extrabold text-[#1E1A3E]">{plan.price}</span>
                    <span className="text-[#6B6690] text-xs">{plan.period}</span>
                    {billingInterval === 'yearly' && plan.savings > 0 && (
                      <p className="text-[11px] text-[#2D2560] mt-1 font-medium">Save ${plan.savings}/year</p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-5 text-left flex-1">
                    {plan.features.map(function (feat, fi) {
                      return (
                        <li key={fi} className="flex items-start gap-2 text-[15px] leading-[20px] text-[#1A1738]">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2D2560]" strokeWidth={2.5} />
                          <span>{feat}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button onClick={function() { plan.cta === 'Start Trial' ? handleTrialCTA(plan.name) : handleCTA(); }}
                    className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-95 mt-auto"
                    style={{ background: 'linear-gradient(135deg, #2D2560, #3A3178, #5A4E9E)', color: '#fff' }}
                    data-testid={'pricing-cta-' + i}>
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== LEGAL + FOOTER ========== */}
      <section className="relative z-10 py-6 px-6" data-testid="legal-section">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/terms" className="text-[#6B6690] hover:text-[#2D2560] text-xs transition-colors underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms of Service</Link>
            <span className="text-[#A8AAD4]">|</span>
            <Link to="/privacy" className="text-[#6B6690] hover:text-[#2D2560] text-xs transition-colors underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy Policy</Link>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#DDE1EE] shadow-sm" data-testid="data-non-sale-badge">
            <Shield className="w-3.5 h-3.5 text-[#2D2560]" />
            <span className="text-xs text-[#2D2560] font-medium">Parentum does not sell, rent, or trade your personal data.</span>
          </div>
        </div>
      </section>

      <footer className="relative z-10 theme-dark bg-[#2D2560] py-6 px-6 border-t border-white/10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto text-center">
          <img src="/parentum-logo.png" alt="Parentum" className="h-5 w-auto max-w-full mx-auto mb-2" />
          <p className="text-xs text-white/70 mb-2 max-w-xl mx-auto leading-relaxed">
            Parentum is an educational and organizational platform. It is not a law firm and does not provide legal advice. No attorney-client relationship is created by using this platform.
          </p>
          <p className="text-xs text-white/50">&copy; 2026 Parentum. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
